import React from "react";
import { useState } from "react";
import { Car, ShieldCheck, Calendar, MapPin } from "lucide-react";
import { ChevronDown, ChevronUp } from "lucide-react";
import { FileText, Image } from "lucide-react";
import DocumentPreviewModal from "../utils/DocumentPreviewModal";

const PolicyDetails = ({ policy, onReupload }) => {
  const [previewUrl, setPreviewUrl] = useState(null);
  const [isExpanded, setIsExpanded] = useState(false);

  const handleOpenPreview = (fileUrl) => {
    setPreviewUrl(fileUrl);
  };

  const handleClosePreview = () => {
    setPreviewUrl(null);
  };

  const toggleExpand = () => {
    setIsExpanded(!isExpanded);
  };

  const renderVehicleDetails = (vehicle) => (
    <div className="bg-gray-50 rounded-lg p-4 mb-4">
      <div className="flex items-center mb-2">
        <Car className="w-5 h-5 mr-2 text-blue-600" />
        <h3 className="font-semibold text-gray-800">Vehicle Details</h3>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <span className="text-gray-600 text-sm">Year</span>
          <p className="font-medium">{vehicle.year}</p>
        </div>
        <div>
          <span className="text-gray-600 text-sm">Make</span>
          <p className="font-medium">{vehicle.make}</p>
        </div>
        <div>
          <span className="text-gray-600 text-sm">Model</span>
          <p className="font-medium">{vehicle.model}</p>
        </div>
        <div>
          <span className="text-gray-600 text-sm">VIN</span>
          <p className="font-medium">{vehicle.vin}</p>
        </div>
      </div>
      {vehicle.coverages && (
        <div className="mt-4">
          <h4 className="text-gray-700 font-semibold mb-2">Coverages</h4>
          {vehicle.coverages.map((coverage, index) => (
            <div key={index} className="bg-white rounded p-2 mb-2 border">
              <div className="flex justify-between">
                <span className="font-medium">{coverage.name}</span>
                <span
                  className={
                    coverage.declined === "DECLINED"
                      ? "text-red-500"
                      : "text-green-500"
                  }
                >
                  {coverage.declined === "DECLINED" ? "Declined" : "Active"}
                </span>
              </div>
              {coverage.premiumCents && (
                <div className="text-sm text-gray-600">
                  Premium: ${(parseInt(coverage.premiumCents) / 100).toFixed(2)}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const renderPolicyHeader = () => (
    <div className="bg-blue-50 rounded-lg p-4 mb-4">
      <div className="flex items-center justify-between mb-2">
        {/* <div className="flex items-center">
          <ShieldCheck className="w-6 h-6 mr-2 text-blue-600" />
          <h2 className="text-xl font-bold text-gray-800">
            {policy.autoInsurance?.documentType || "Insurance Policy"}
          </h2>
        </div> */}
        <div className="space-y-2">
          <div className="flex items-center space-x-2">
            <h3 className="font-semibold text-gray-800">
              {policy?.autoInsurance?.carrierName || "Unknown"}
            </h3>
            <p className="font-medium">
              <div>
                <div className="text-gray-600 mr-2">Valid Through:</div>
                {new Date(
                  policy.autoInsurance?.expiryDate
                ).toLocaleDateString()}
              </div>
              {/* {new Date(policy.autoInsurance?.expiryDate).toLocaleDateString()} */}
            </p>
            {/* Expired Badge */}
            {/* {policy.expired && (
              <div className="px-3 py-1 rounded-full text-sm font-medium bg-red-100 text-red-800">
                Expired
              </div>
            )} */}

            {/* Status Badge */}
            <div
              className={`px-3 py-1 rounded-full text-sm font-medium ${
                policy.checkInStatus === "Confirmed"
                  ? "bg-green-100 text-green-800"
                  : policy.checkInStatus === "Pending"
                  ? "bg-yellow-100 text-yellow-800"
                  : policy.checkInStatus === "OnHold"
                  ? "bg-orange-100 text-orange-800"
                  : policy.checkInStatus === "Declined"
                  ? "bg-red-100 text-red-800"
                  : "bg-gray-100 text-gray-800"
              }`}
            >
              {policy.checkInStatus || "Unknown"}
            </div>

            {/* Expand/Collapse Button */}
            <button
              onClick={toggleExpand}
              className="text-gray-600 hover:text-gray-900"
            >
              {isExpanded ? (
                <ChevronUp className="w-6 h-6" />
              ) : (
                <ChevronDown className="w-6 h-6" />
              )}
            </button>
          </div>

          {/* Reupload Button for Confirmed and Expired Policies */}
          {/* {policy.expired && policy.checkInStatus === "Confirmed" && (
            <div>
              <button
                onClick={() => onReupload(policy)}
                className="px-3 py-1 bg-blue-500 text-white rounded-full text-sm hover:bg-blue-600 transition-colors"
              >
                Reupload
              </button>
            </div>
          )} */}
        </div>
      </div>
      {/* Rest of the existing code */}
    </div>
  );

  const renderPolicyDates = () => (
    <div className="bg-gray-50 rounded-lg p-4 mb-4">
      <div className="flex items-center mb-2">
        <Calendar className="w-5 h-5 mr-2 text-blue-600" />
        <h3 className="font-semibold text-gray-800">Policy Dates</h3>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <span className="text-gray-600 text-sm">Effective Date</span>
          <p className="font-medium">
            {new Date(policy.autoInsurance?.effectiveDate).toLocaleDateString()}
          </p>
        </div>
        <div>
          <span className="text-gray-600 text-sm">Expiry Date</span>
          <p className="font-medium">
            {new Date(policy.autoInsurance?.expiryDate).toLocaleDateString()}
          </p>
        </div>
      </div>
    </div>
  );

  const renderAddressDetails = () => {
    const address = policy.autoInsurance?.addresses?.[0];
    return address ? (
      <div className="bg-gray-50 rounded-lg p-4 mb-4">
        <div className="flex items-center mb-2">
          <MapPin className="w-5 h-5 mr-2 text-blue-600" />
          <h3 className="font-semibold text-gray-800">Address</h3>
        </div>
        <p className="font-medium">{address.fullAddress}</p>
        <div className="grid grid-cols-2 gap-2 mt-2">
          <div>
            <span className="text-gray-600 text-sm">City</span>
            <p className="font-medium">{address.city}</p>
          </div>
          <div>
            <span className="text-gray-600 text-sm">State</span>
            <p className="font-medium">{address.state}</p>
          </div>
        </div>
      </div>
    ) : null;
  };

  return (
    <div className="space-y-4 p-4">
      {renderPolicyHeader()}

      {isExpanded && (
        <>
          {renderPolicyDates()}
          {renderAddressDetails()}
          {policy.autoInsurance?.vehicles?.map((vehicle, index) =>
            renderVehicleDetails(vehicle)
          )}
          {/* Document Preview Section */}
          {policy.files.map((file, index) => (
            <div
              key={index}
              className="border rounded-lg p-2 flex items-center justify-between"
            >
              <div className="flex items-center">
                {file.documentType === "DEC_AUTO" ? (
                  <FileText className="w-6 h-6 mr-2 text-blue-600" />
                ) : (
                  <Image className="w-6 h-6 mr-2 text-green-600" />
                )}
                <span className="text-sm">
                  {file.url.split("/").pop().split("_").pop() ||
                    "Insurance Document"}
                </span>
              </div>
              <button
                onClick={() => handleOpenPreview(file.url)}
                className="text-blue-600 hover:text-blue-800 text-sm"
              >
                Preview
              </button>
            </div>
          ))}
        </>
      )}
      {/* Document Preview Modal */}
      <DocumentPreviewModal
        isOpen={!!previewUrl}
        onClose={handleClosePreview}
        fileUrl={previewUrl}
      />
    </div>
  );
};
export default PolicyDetails;
